import type { Format, StandingsFormat, StandingsRow } from '@foosball/shared';
import { useState } from 'react';
import { Link } from 'react-router';

import { Card } from '../components/ui';
import { useAwaitingMe, useSeasons, useStandings } from '../hooks/useMatches';
import { useAuth } from '../lib/auth-context';

const FORMATS: { label: string; value: StandingsFormat }[] = [
  { label: 'Combined', value: 'all' },
  { label: '1v1', value: '1v1' },
  { label: '2v2', value: '2v2' },
];

function ratingFor(row: StandingsRow, format: Format): number | null {
  return row.ratings.find((r) => r.format === format)?.rating ?? null;
}

function gamesFor(row: StandingsRow, format: Format): number {
  return row.ratings.find((r) => r.format === format)?.gamesPlayed ?? 0;
}

export function LeagueTablePage() {
  const { user } = useAuth();
  const seasons = useSeasons();
  const awaiting = useAwaitingMe();

  const [format, setFormat] = useState<StandingsFormat>('all');
  const [seasonId, setSeasonId] = useState<number | null>(null);

  // Default to the active season, or the most recent one if none is active.
  const resolvedSeasonId =
    seasonId ?? seasons.data?.find((s) => s.isActive)?.id ?? seasons.data?.[0]?.id ?? null;

  const standings = useStandings(resolvedSeasonId, format);
  const pending = awaiting.data?.count ?? 0;

  // The Elo ladder is per-format; under "Combined" show singles, which is the
  // format most leagues care about, with doubles alongside it.
  const ladderFormat: Format = format === '2v2' ? '2v2' : '1v1';
  const ladder = [...(standings.data?.rows ?? [])].sort(
    (a, b) => (ratingFor(b, ladderFormat) ?? 0) - (ratingFor(a, ladderFormat) ?? 0),
  );

  return (
    <>
      <h1 className="mb-4 text-2xl font-bold tracking-tight">League table</h1>

      {pending > 0 && (
        <Link to="/confirmations" className="mb-4 block">
          <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {pending} result{pending === 1 ? '' : 's'} awaiting your confirmation
            </p>
          </Card>
        </Link>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex-1">
          <span className="sr-only">Season</span>
          <select
            aria-label="Season"
            value={resolvedSeasonId ?? ''}
            onChange={(e) => setSeasonId(Number(e.target.value))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          >
            {(seasons.data ?? []).map((season) => (
              <option key={season.id} value={season.id}>
                {season.name}
                {season.isActive ? ' (current)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        role="group"
        aria-label="Format"
        className="mb-4 grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800"
      >
        {FORMATS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={format === option.value}
            onClick={() => setFormat(option.value)}
            className={`rounded-md py-1.5 text-sm font-semibold transition ${
              format === option.value
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-slate-100'
                : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/*
        A disabled query (no season to fetch for) reports `isPending` forever,
        so the empty state must be checked first or the page shows a permanent
        "Loading…" when the league has no seasons at all.
      */}
      {seasons.isSuccess && resolvedSeasonId === null ? (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No season yet. An administrator needs to create one.
          </p>
        </Card>
      ) : standings.isPending ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : standings.isError || !standings.data ? (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Could not load the standings.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <section aria-labelledby="points-heading">
            <h2
              id="points-heading"
              className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Points table
            </h2>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 dark:text-slate-400">
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th scope="col" className="px-3 py-2 text-left font-medium">
                      Player
                    </th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">
                      P
                    </th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">
                      W
                    </th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">
                      L
                    </th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">
                      GD
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Pts
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {standings.data.rows.map((row, index) => (
                    <tr
                      key={row.userId}
                      className={`border-b border-slate-100 last:border-0 dark:border-slate-800 ${
                        row.userId === user?.id ? 'bg-emerald-50 dark:bg-emerald-950/40' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <span className="mr-2 text-xs text-slate-400 tabular-nums">{index + 1}</span>
                        <Link to={`/players/${row.userId}`} className="hover:underline">
                          {row.displayName}
                        </Link>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.played}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.won}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.lost}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {row.goalDiff > 0 ? '+' : ''}
                        {row.goalDiff}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {row.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section aria-labelledby="ladder-heading">
            <h2
              id="ladder-heading"
              className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Elo ladder · {ladderFormat}
            </h2>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 dark:text-slate-400">
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th scope="col" className="px-3 py-2 text-left font-medium">
                      Player
                    </th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">
                      Games
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Rating
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ladder.map((row, index) => (
                    <tr
                      key={row.userId}
                      className={`border-b border-slate-100 last:border-0 dark:border-slate-800 ${
                        row.userId === user?.id ? 'bg-emerald-50 dark:bg-emerald-950/40' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <span className="mr-2 text-xs text-slate-400 tabular-nums">{index + 1}</span>
                        <Link to={`/players/${row.userId}`} className="hover:underline">
                          {row.displayName}
                        </Link>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-500">
                        {gamesFor(row, ladderFormat)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {ratingFor(row, ladderFormat) ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              Elo carries across seasons; points reset each season.
            </p>
          </section>
        </div>
      )}
    </>
  );
}
